"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLayoutDashboard, IconLogin, IconMenu2, IconMoon, IconSun } from "@tabler/icons-react";
import Link from "next/link";
import { AppHeaderSeparator } from "@/components/layout/app-header-separator";
import { UserMenu } from "@/components/layout/user-menu";
import { NotificationsPopover } from "@/components/notifications/notifications-popover";
import { useTheme } from "@/hooks/use-theme";
import { useSession } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@domainstack/ui/dropdown-menu";
import { useIsClient } from "@domainstack/ui/hooks";
import { Skeleton } from "@domainstack/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";

/**
 * Session-dependent header buttons that conditionally render based on auth state.
 * Client component that uses useSession hook to avoid turning pages into PPR.
 */
export function AppHeaderAccount() {
  const $ = _c(28);
  const {
    data: session,
    isPending
  } = useSession();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const mounted = useIsClient();
  if (!mounted || isPending) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Skeleton className="size-8 rounded-md max-md:ml-1" /><AppHeaderSeparator className="hidden md:block" /><Skeleton className="hidden h-8 w-[4.75rem] rounded-md md:block" /></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (session?.user) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Tooltip><TooltipTrigger render={<Button variant="ghost" size="icon-sm" nativeButton={false} render={<Link href="/dashboard"><IconLayoutDashboard /><span className="sr-only">Dashboard</span></Link>} />} /><TooltipContent>Dashboard</TooltipContent></Tooltip><AppHeaderSeparator /><NotificationsPopover /><AppHeaderSeparator className="mr-2" /><UserMenu /></>;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Menu" className="md:hidden"><IconMenu2 /></Button>} />;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== theme) {
    t1 = theme === "dark" ? <IconSun /> : <IconMoon />;
    $[3] = theme;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = theme === "dark" ? "Light mode" : "Dark mode";
  let t3;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== toggleTheme) {
    t3 = <DropdownMenuItem onClick={toggleTheme} closeOnClick={false}>{t1}{t2}</DropdownMenuItem>;
    $[5] = t1;
    $[6] = t2;
    $[7] = toggleTheme;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DropdownMenuSeparator />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DropdownMenuItem nativeButton={false} render={<Link href="/login" scroll={false}><IconLogin />Sign In</Link>} />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3) {
    t6 = <DropdownMenu>{t0}<DropdownMenuContent align="end" className="min-w-48">{t3}{t4}{t5}</DropdownMenuContent></DropdownMenu>;
    $[11] = t3;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IconSun className="scale-100 rotate-0 transition-[transform,opacity] dark:scale-95 dark:-rotate-90 dark:opacity-0" />;
    t8 = <IconMoon className="absolute scale-95 rotate-90 opacity-0 transition-[transform,opacity] dark:scale-100 dark:rotate-0 dark:opacity-100" />;
    t9 = <span className="sr-only">Toggle theme</span>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== toggleTheme) {
    t10 = <TooltipTrigger render={<Button aria-label="Toggle theme" variant="ghost" size="sm" onClick={toggleTheme} className="hidden md:inline-flex">{t7}{t8}{t9}</Button>} />;
    $[16] = toggleTheme;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = theme === "dark" ? "Dark mode" : "Light mode";
  let t12;
  if ($[18] !== t11) {
    t12 = <TooltipContent>{t11}</TooltipContent>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <Tooltip>{t10}{t12}</Tooltip>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <AppHeaderSeparator className="hidden md:block" />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Button variant="ghost" size="sm" nativeButton={false} aria-label="Sign In" className="hidden md:inline-flex" render={<Link href="/login" scroll={false}>Sign In</Link>} />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t6) {
    t16 = <>{t6}{t13}{t14}{t15}</>;
    $[25] = t13;
    $[26] = t6;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  return t16;
}